import { useForm, useController } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useEffect, useMemo } from 'react'

import { usePage } from '@inertiajs/react'
import useMutation from '@hooks/useGQLMutation'
import useQuery from '@hooks/useQuery'
import { LogContactDocument } from '@frontend/graphql/mutations/logContact.generated'
import { GetCallListsForGroupDocument } from '@frontend/graphql/queries/callLists.generated'
import { GetEventsForRsvpDocument } from '@frontend/graphql/queries/events.generated'
import type { EventsForRsvpQueryType } from '@frontend/graphql/queries/events'
import { EventRsvpAction } from '@frontend/graphql/types.generated'
import { useAlertsStore } from '@stores/alertsStore'
import { toast } from '@stores/toastStore'
import { EventRsvpPicker, type EventRsvp } from '@components/molecules/EventRsvpPicker'
import { FormDialog } from '@components/molecules/FormDialog'

import { i18nValidation, scopedTranslation } from '@frontend/utils/I18n'
import { SelectField } from '@components/molecules/SelectField'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { Textarea } from '@atoms/form/Textarea'
import Checkbox from '@atoms/form/Checkbox'
import TagsSelect from '@components/molecules/TagsSelect'
import type { LogContactDialogState } from '../../types/dialogTypes'
import type { SharedInertiaProps } from '../../types/groupTypes'

const t = scopedTranslation('components.log_contact_dialog')
const tAttributes = scopedTranslation('attributes.contact')

const ALERTS_PATH = 'logContact' as const

const createSchema = (canCompleteCall: boolean) => yup.object({
  contactMethod: yup
    .string()
    .label(tAttributes('contact_method'))
    .required(i18nValidation('required')),
  contactStatus: yup
    .string()
    .label(tAttributes('contact_status'))
    .required(i18nValidation('required')),
  outcome: yup
    .string()
    .label(tAttributes('outcome'))
    .nullable()
    .when(['callCompleted', 'contactMethod'], {
      is: (callCompleted: boolean, contactMethod: string) =>
        canCompleteCall && callCompleted && contactMethod === 'phone_call',
      then: (schema) => schema.required(i18nValidation('required')),
      otherwise: (schema) => schema.optional()
    }),
  connected: yup
    .boolean()
    .label(tAttributes('connected'))
    .optional(),
  callCompleted: yup
    .boolean()
    .label(tAttributes('call_completed'))
    .optional(),
  note: yup
    .string()
    .label(tAttributes('note'))
    .optional(),
  tags: yup
    .array()
    .of(
      yup.object({
        value: yup.string().required(),
        label: yup.string().required(),
      })
    )
    .optional(),
  eventRsvps: yup
    .array()
    .of(
      yup.object({
        eventId: yup.string().required(),
        action: yup.mixed<EventRsvpAction>().oneOf(Object.values(EventRsvpAction)).required(),
        shiftIds: yup.array().of(yup.string().required()).optional(),
      })
    )
    .required(),
  callListId: yup
    .string()
    .optional()
})

const getContactMethodOptions = () => [
  { value: 'phone_call', label: t('form.options.contact_methods.phone_call') },
  { value: 'email', label: t('form.options.contact_methods.email') },
  { value: 'text', label: t('form.options.contact_methods.text') },
  { value: 'face_to_face', label: t('form.options.contact_methods.face_to_face') }
]

const getContactStatusOptions = () => [
  { value: 'answered', label: t('form.options.contact_statuses.answered') },
  { value: 'bad_info', label: t('form.options.contact_statuses.bad_info') },
  { value: 'inaccessible', label: t('form.options.contact_statuses.inaccessible') },
  { value: 'left_message', label: t('form.options.contact_statuses.left_message') },
  { value: 'meaningful_interaction', label: t('form.options.contact_statuses.meaningful_interaction') },
  { value: 'not_interested', label: t('form.options.contact_statuses.not_interested') },
  { value: 'no_answer', label: t('form.options.contact_statuses.no_answer') },
  { value: 'refused', label: t('form.options.contact_statuses.refused') },
  { value: 'send_information', label: t('form.options.contact_statuses.send_information') },
  { value: 'other', label: t('form.options.contact_statuses.other') }
]

const getOutcomeOptions = () => [
  { value: 'successful', label: t('form.options.outcomes.successful') },
  { value: 'unsuccessful', label: t('form.options.outcomes.unsuccessful') }
]

type FormData = {
  contactMethod: string
  contactStatus: string
  outcome?: string | null
  connected?: boolean
  callCompleted?: boolean
  note?: string
  tags?: Array<{ value: string; label: string }>
  eventRsvps: EventRsvp[]
  callListId?: string
  callingSessionSupporterId?: string
}

type LogContactDialogProps = {
  onClose: () => void
  dialogState: LogContactDialogState
}

export type LogContactPermissions = Pick<
  SharedInertiaProps['permissions'],
  'showAllTags' | 'showConnectedField' | 'showCallListInLogContactForm'
>

type LogContactDialogContentProps = LogContactDialogProps & {
  permissions: LogContactPermissions
}


type CallListsData = {
  callLists: Array<{
    id: string
    name: string
  }>
}

export function LogContactDialog({ onClose, dialogState }: LogContactDialogProps) {
  const { props: { permissions } } = usePage<SharedInertiaProps>()

  return <LogContactDialogContent onClose={onClose} dialogState={dialogState} permissions={permissions ?? {}} />
}

export function LogContactDialogContent({
  onClose,
  dialogState,
  permissions,
}: LogContactDialogContentProps) {
  const { dialogData } = dialogState
  const supporterId = dialogData.id
  const supporterName = `${dialogData.firstName || ''} ${dialogData.lastName || ''}`.trim()
  const groupId = dialogData.groupId
  const callingSessionSupporterId = dialogData.callingSessionSupporterId
  const callListPersonId = dialogData.callListPersonId
  const canCompleteCall = !!(callingSessionSupporterId || callListPersonId)
  const preselectPhoneCall = dialogData.isPhoneCall || canCompleteCall
  const hasGroup = !!groupId
  const canAddToCallList = !!permissions.showCallListInLogContactForm && hasGroup && !callingSessionSupporterId
  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue
  } = useForm<FormData>({
    resolver: yupResolver(createSchema(canCompleteCall)),
    defaultValues: {
      connected: false,
      eventRsvps: [],
      contactMethod: preselectPhoneCall ? 'phone_call' : undefined,
      callingSessionSupporterId: dialogData.callingSessionSupporterId
    }
  })

  const selectedContactMethod = watch('contactMethod')
  const callCompleted = watch('callCompleted')
  const isPhoneCallMethod = selectedContactMethod === 'phone_call'

  useEffect(() => {
    if (!isPhoneCallMethod) {
      setValue('outcome', null)
      setValue('connected', undefined)
      setValue('callCompleted', undefined)
    }
  }, [isPhoneCallMethod, setValue])

  useEffect(() => {
    if (!callCompleted) {
      setValue('outcome', null)
    }
  }, [callCompleted, setValue])

  const logContactMutationHook = useMutation(LogContactDocument)

  const eventsQueryHook = useQuery<EventsForRsvpQueryType>({
    key: ['eventsForRsvp', supporterId],
    query: GetEventsForRsvpDocument,
    variables: { supporterId },
    alwaysFresh: true
  })

  const callListsQueryHook = useQuery<CallListsData>({
    key: 'callListsForGroup',
    query: GetCallListsForGroupDocument,
    variables: { groupId },
    enabled: hasGroup
  })

  const events = eventsQueryHook.data?.eventsForRsvp?.nodes || []

  const callLists = useMemo(() => {
    return (callListsQueryHook.data?.callLists || []).map(list => ({
      value: list.id,
      label: list.name
    }))
  }, [callListsQueryHook.data])

  const onSubmit = (data: FormData) => {
    clearAlerts(ALERTS_PATH)

    const mutationData = {
      supporterId,
      groupId,
      contactMethod: data.contactMethod,
      contactStatus: data.contactStatus,
      outcome: isPhoneCallMethod && data.callCompleted ? data.outcome : null,
      connected: isPhoneCallMethod ? data.connected : null,
      callCompleted: isPhoneCallMethod ? data.callCompleted : null,
      callingSessionSupporterId: callingSessionSupporterId,
      callListPersonId,
      note: data.note,
      tags: data.tags?.map((tag) => tag.value),
      eventRsvps: data.eventRsvps,
      callListId: data.callListId,
    }

    logContactMutationHook.mutate(mutationData, {
      onSuccess: (response) => {
        if (response?.logContact?.success) {
          reset()
          clearAlerts(ALERTS_PATH)
          onClose()
          toast.success(t('alerts.success.title'))
        } else {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: response?.logContact?.errors?.map((error: { message: string }) => ({
              type: 'error',
              details: {
                title: error.message
              }
            })) || []
          })
        }
      },
      onError: () => {
        setAlerts({
          alertsPath: ALERTS_PATH,
          alerts: [{
            type: 'error',
            details: {
              title: t('errors.log_failed')
            }
          }]
        })
      }
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset()
      clearAlerts(ALERTS_PATH)
      onClose()
    }
  }

  const contactMethodField = useController({ name: 'contactMethod', control }).field
  const contactStatusField = useController({ name: 'contactStatus', control }).field
  const outcomeField = useController({ name: 'outcome', control }).field
  const callListField = useController({ name: 'callListId', control }).field
  const eventRsvpsField = useController({ name: 'eventRsvps', control }).field

  return (
    <FormDialog.Root
      open={true}
      onOpenChange={handleOpenChange}
      testId="log-contact-dialog"
      preventOutsideClick
    >
      <FormDialog.Title alertsPath={ALERTS_PATH}>{t('title', { name: supporterName })}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(onSubmit)}>
        <FormDialog.Body>
          <RadioGroupField
            name={contactMethodField.name}
            ref={contactMethodField.ref}
            value={contactMethodField.value}
            onValueChange={contactMethodField.onChange}
            label={tAttributes('contact_method')}
            options={getContactMethodOptions()}
            errors={errors}
            required
          />

          <SelectField
            name={contactStatusField.name}
            ref={contactStatusField.ref}
            value={contactStatusField.value}
            onValueChange={contactStatusField.onChange}
            label={tAttributes('contact_status')}
            placeholder={t('form.placeholders.contact_status')}
            options={getContactStatusOptions()}
            errors={errors}
            required
          />

          <EventRsvpPicker
            events={events}
            value={eventRsvpsField.value}
            onChange={eventRsvpsField.onChange}
            disabled={eventsQueryHook.isLoading}
          />

          {isPhoneCallMethod && (
            <>
              {permissions.showConnectedField && (
                <div className={'tw:flex tw:flex-wrap tw:items-center tw:w-full tw:pt-2'}>
                  <Checkbox
                    name="connected"
                    control={control}
                    errors={errors}
                    defaultChecked={false}
                    description={t('form.labels.connected_hint')}
                  >
                    {tAttributes('connected')}
                  </Checkbox>
                </div>
              )}

              {canCompleteCall && (
                <div className={'tw:flex tw:flex-wrap tw:items-center tw:w-full tw:pt-2'}>
                  <Checkbox
                    name="callCompleted"
                    control={control}
                    errors={errors}
                    defaultChecked={false}
                    description={t('form.labels.call_completed_hint')}
                  >
                    {tAttributes('call_completed')}
                  </Checkbox>
                </div>
              )}

              {canCompleteCall && callCompleted && (
                <div
                  data-testid="outcome-field"
                  className="tw:motion-safe:animate-in tw:motion-safe:fade-in-0 tw:motion-safe:slide-in-from-top-1 tw:motion-safe:duration-150"
                >
                  <RadioGroupField
                    name={outcomeField.name}
                    ref={outcomeField.ref}
                    value={outcomeField.value}
                    onValueChange={outcomeField.onChange}
                    label={tAttributes('outcome')}
                    options={getOutcomeOptions()}
                    errors={errors}
                    required
                  />
                </div>
              )}
            </>
          )}

          {/* Wrapper div needed because TagsSelect returns a fragment (label + select) 
              and FormDialog.Body applies tw:space-y-4 which would create gaps between them */}
          {permissions.showAllTags && (
            <div>
              <TagsSelect
                control={control}
                name="tags"
                createOptionPrefix={t('form.labels.create_tag')}
                searchPlaceholder={t('form.placeholders.tags_search')}
              />
            </div>
          )}

          {canAddToCallList && (
            <div>
              {callLists.length > 0 ? (
                <SelectField
                  name={callListField.name}
                  value={callListField.value}
                  onValueChange={(callListId) => callListField.onChange(callListId ?? undefined)}
                  emptyOptionLabel={t('form.placeholders.no_call_list')}
                  label={t('form.labels.call_list')}
                  description={t('form.descriptions.call_list')}
                  options={callLists}
                  errors={errors}
                  disabled={callListsQueryHook.isLoading}
                />
              ) : !callListsQueryHook.isLoading && (
                <div>
                  <label className='tw:block tw:text-sm tw:font-medium tw:text-gray-700'>
                    {t('form.labels.call_list')}
                  </label>
                  <p className='tw:text-sm tw:text-gray-500 tw:mt-1'>
                    {t('form.messages.no_call_lists')}
                  </p>
                </div>
              )}
            </div>
          )}

          <Textarea
            label={tAttributes('note')}
            errors={errors}
            placeholder={t('form.placeholders.note')}
            rows={4}
            {...register('note')}
          />
        </FormDialog.Body>
        <FormDialog.Footer
          submitText={t('form.log_contact')}
          isLoading={logContactMutationHook.isLoading}
        />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
